import { CSSProperties, useCallback } from 'react';

import { AutoSizer } from 'react-virtualized-auto-sizer';
import { List, ListProps } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';

import { useRowIdHeightCache } from './useRowIdHeightCache';

interface RowInfiniteLoaderBaseProps<RowProps extends object> {
  rowComponent: ListProps<RowProps>['rowComponent'];
  rowProps: ListProps<RowProps>['rowProps'];
  itemCount: number;
  loadMoreItems: (startIndex: number, stopIndex: number) => void;
  isItemLoaded: (index: number) => boolean;
  itemSize: number;
  className?: string;
  style?: CSSProperties;
  /** Imperative handle to the underlying list; exposes its scroll element via `.element`. */
  listRef?: ListProps<RowProps>['listRef'];
}

/** Dynamic heights need `rowId`: a cache keyed by slot index goes stale as soon as rows move. */
type RowIdentityProps =
  | { dynamicRowHeight: true; rowId: (index: number) => string }
  | { dynamicRowHeight?: false; rowId?: (index: number) => string };

export type RowInfiniteLoaderProps<RowProps extends object> = RowInfiniteLoaderBaseProps<RowProps> &
  RowIdentityProps;

export const RowInfiniteLoader = <RowProps extends object>({
  rowComponent,
  rowProps,
  rowId,
  itemCount,
  loadMoreItems,
  isItemLoaded,
  itemSize,
  dynamicRowHeight,
  className,
  style,
  listRef,
}: RowInfiniteLoaderProps<RowProps>) => {
  const onRowsRendered = useInfiniteLoader({
    isRowLoaded: isItemLoaded,
    loadMoreRows: async (startIndex, stopIndex) => loadMoreItems(startIndex, stopIndex),
    rowCount: itemCount,
  });

  // Stands in for fixed-height lists, where the cache is built but never read.
  const identity = useCallback((index: number) => `${index}`, []);
  const heightCache = useRowIdHeightCache({ defaultRowHeight: itemSize, rowId: rowId ?? identity });
  const rowHeight = dynamicRowHeight ? heightCache : itemSize;

  return (
    <AutoSizer
      renderProp={({ height, width }) => {
        if (height === undefined || width === undefined) {
          return null;
        }
        return (
          <List
            listRef={listRef}
            className={className}
            rowComponent={rowComponent}
            rowProps={rowProps}
            rowKey={rowId}
            rowCount={itemCount}
            rowHeight={rowHeight}
            onRowsRendered={onRowsRendered}
            style={{ height, width, overflowX: 'hidden', ...style }}
          />
        );
      }}
    />
  );
};
